import { BrowserRouter, NavLink, Navigate, Route, Routes } from 'react-router-dom';
import { MapPage } from './pages/MapPage';
import { RecordPage } from './pages/RecordPage';
import { AboutPage } from './pages/AboutPage';
import { PrivacyPage } from './pages/PrivacyPage';
import { SharePage } from './pages/SharePage';
import { DownloadPage } from './pages/DownloadPage';
import { ControlPage } from './pages/ControlPage';
import { useEffect, useState } from 'react';
import { detectRuntime } from './platform/runtime';
import { startAmbientUploader } from './platform/upload-service';
import { checkAuth } from './platform/auth-check';
import { startVersionSync } from './platform/version-sync';
import { UpdateAvailableBanner } from './components/UpdateAvailableBanner';
import { AppFooter } from './components/AppFooter';
import { HeaderDownloadMenu } from './components/HeaderDownloadMenu';

export function App() {
  // The ambient uploader drains queued events whenever the app is open,
  // not just while RecordPage is on screen. It reacts to online +
  // visibility-change events for immediate flushes.
  useEffect(() => {
    startAmbientUploader();
  }, []);

  // Watch for new deploys + admin "reload everyone" pushes. Reloads
  // silently when idle; surfaces a banner via UpdateAvailableBanner
  // when the user is mid-trip so we don't kill an active recording.
  const [updatePending, setUpdatePending] = useState(false);
  useEffect(() => {
    const sync = startVersionSync({
      onPending: () => setUpdatePending(true),
    });
    return () => sync.stop();
  }, []);

  // Detect whether the visitor is logged-in AND in access-manager's
  // allowlist for joltstream. Result determines whether the Admin
  // nav link is shown. Public visitors never see the link.
  const [showAdmin, setShowAdmin] = useState(false);
  useEffect(() => {
    void checkAuth().then((s) => setShowAdmin(s.authorised));
  }, []);

  // The Android APK (Capacitor wrap) is a single-purpose trip recorder —
  // no top nav, no map, no download, no control room. The footer with
  // About + Privacy is the one piece of chrome that does belong here.
  if (detectRuntime() === 'capacitor') {
    return (
      <BrowserRouter>
        {updatePending && <UpdateAvailableBanner />}
        <Routes>
          <Route path="/about" element={<AboutPage />} />
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route path="*" element={<RecordPage />} />
        </Routes>
        <AppFooter />
      </BrowserRouter>
    );
  }

  return (
    <BrowserRouter>
      {updatePending && <UpdateAvailableBanner />}
      <header className="app-header">
        {/* Left cluster: brand + adjacent Share affordance.
           Share lives next to the brand rather than in the primary nav
           so it's discoverable but visually subordinate. */}
        <div className="app-header-left">
          <NavLink to="/" className="brand">
            <img src="/favicon.svg" alt="" width="28" height="28" aria-hidden="true" />
            <span>joltstream</span>
          </NavLink>
          <NavLink to="/share" className="header-share-icon" aria-label="Share joltstream">
            <span aria-hidden="true">↗</span>
            <span>Share</span>
          </NavLink>
        </div>
        {/* Right cluster: primary nav (Stream Data + Map + Admin when
           authorised) plus the Download dropdown menu. About + Privacy
           are intentionally NOT here — they live in the footer. */}
        <nav className="app-header-nav">
          <NavLink to="/" end>Stream Data</NavLink>
          <NavLink to="/map">Map</NavLink>
          {showAdmin && <NavLink to="/admin/control">Admin</NavLink>}
          <HeaderDownloadMenu />
        </nav>
      </header>
      <main>
        <Routes>
          {/* Default landing is the Stream Data page — the mission is
             contribution, not spectating. Map is at /map; /record stays
             as a redirect for any bookmarks. */}
          <Route path="/" element={<RecordPage />} />
          <Route path="/map" element={<MapPage />} />
          <Route path="/record" element={<Navigate to="/" replace />} />
          <Route path="/download" element={<DownloadPage />} />
          {/* /admin/* is the auth-walled admin surface. /control stays as a
             redirect for any bookmarked URLs from the previous routing.
             Bare /admin (no /control suffix) also redirects — without
             this it falls through to the `*` catch-all. */}
          <Route path="/control" element={<Navigate to="/admin/control" replace />} />
          <Route path="/admin" element={<Navigate to="/admin/control" replace />} />
          <Route path="/admin/control" element={<ControlPage />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route path="/share" element={<SharePage />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </main>
      <AppFooter />
    </BrowserRouter>
  );
}
